/**
 * org-chart-scale.ts
 *
 * Auto-escala div.org-chart para caber na largura do container.
 * Usa a propriedade CSS `zoom` — ao contrário de transform:scale, o zoom
 * afeta o layout (o elemento encolhe de verdade, sem overflow nem clipping).
 *
 * Medição:
 *   O ul raiz é display:flex + nowrap — seu offsetWidth é limitado pelo pai,
 *   NÃO pela largura real da árvore. Para medir a largura intrínseca usamos
 *   `width: max-content` temporariamente, lemos offsetWidth, e restauramos.
 *
 * Anti-loop (ResizeObserver):
 *   Armazena o último ratio calculado em data-oc-zoom. Se o novo ratio ≈ igual
 *   ao anterior, reutiliza sem re-aplicar, evitando reset → resize → loop.
 *
 * Ativado em initOrgChartScale() — chamado no app.ts após DOMContentLoaded.
 */

type ZoomStyle = CSSStyleDeclaration & { zoom: string };

const MIN_ZOOM = 0.3; // piso: nunca reduz abaixo de 30%

/** Aplica (ou remove) o zoom shrink-to-fit em um único .org-chart */
function scaleChart(chart: HTMLElement): void {
    const ul = chart.querySelector<HTMLElement>(':scope > ul');
    if (!ul) return;

    const zs = ul.style as ZoomStyle;

    // 1. Reseta zoom + força largura intrínseca para medir o tamanho real.
    //    Tudo síncrono — ResizeObserver não dispara até o próximo frame.
    zs.zoom = '';
    ul.style.width = 'max-content';
    const naturalW = ul.offsetWidth;
    ul.style.width = '';

    // 2. Espaço disponível = clientWidth menos padding horizontal
    const cs = window.getComputedStyle(chart);
    const padH = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const avail = chart.clientWidth - padH;

    if (avail <= 0 || naturalW <= avail) {
        // Cabe sem zoom
        chart.removeAttribute('data-oc-zoom');
        return;
    }

    // 3. Calcula ratio (com piso de segurança)
    const ratio = Math.max(avail / naturalW, MIN_ZOOM);

    // 4. Anti-loop: se o ratio é o mesmo da última passada, reutiliza
    const prev = chart.getAttribute('data-oc-zoom');
    if (prev && Math.abs(parseFloat(prev) - ratio) < 0.005) {
        zs.zoom = prev;
        return;
    }

    // 5. Aplica e persiste
    chart.setAttribute('data-oc-zoom', String(ratio));
    zs.zoom = String(ratio);
}

/** Escala imediatamente e observa redimensionamentos futuros */
function attachChart(chart: HTMLElement, ro: ResizeObserver): void {
    scaleChart(chart);
    ro.observe(chart);
}

/** Ponto de entrada — chamar após DOMContentLoaded */
export function initOrgChartScale(): void {
    const ro = new ResizeObserver(entries => {
        for (const entry of entries) {
            scaleChart(entry.target as HTMLElement);
        }
    });

    // Escala todos os org-charts já presentes no DOM
    document.querySelectorAll<HTMLElement>('.org-chart')
        .forEach(c => attachChart(c, ro));

    // Observa novos org-charts inseridos dinamicamente
    // (ex: modal da biblioteca, editor visual, preview)
    new MutationObserver(mutations => {
        for (const m of mutations) {
            m.addedNodes.forEach(node => {
                if (!(node instanceof HTMLElement)) return;

                if (node.classList.contains('org-chart')) {
                    attachChart(node, ro);
                    return;
                }
                node.querySelectorAll<HTMLElement>('.org-chart')
                    .forEach(c => attachChart(c, ro));
            });
        }
    }).observe(document.body, { childList: true, subtree: true });
}
